import { FieldGroup, FormLines, StaticField, useComputed, useTestIds } from "@homebound/beam";
import { Price } from "src/components/Price";
import { ChangeEventType } from "src/generated/graphqlTypes";
import { useCurrentUser } from "src/hooks/useCurrentUser";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { count, pluralize, queryResult } from "src/utils";
import {
  ReviewProjectImpact_ProjectCostCodeFragment,
  useReviewProjectImpactComputeCostQuery,
} from "../3.ReviewImpactStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import { createProjectBudgetRows, reviewImpactBudgetTotals } from "../reviewImpactUtils";
import { mockScheduleImpactMilestones } from "./ScheduleImpactTable";

type ApplyConfigurationSummaryStatsProps = {
  formState: WorkflowFormState<ApplyConfigurationFormInput>;
  /** What this project's lot type calls its client, i.e. "Homeowner", for the Chargeable label. */
  clientNoun: string;
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[];
  readyPlanId: string;
  readyPlanOptionIds: string[];
};

/**
 * The summary of what Apply will do, for a project with no signed contract.
 *
 * There is no change event to read the impact off of, so the numbers are computed from the selected configuration,
 * exactly as the Review Impact step does it.
 */
export function ApplyConfigurationSummaryStats(props: ApplyConfigurationSummaryStatsProps) {
  const { formState, clientNoun, projectCostCodes, readyPlanId, readyPlanOptionIds } = props;
  const isChargeable = useComputed(() => formState.type.value === ChangeEventType.External, [formState]);
  const currentUser = useCurrentUser();
  const tid = useTestIds(props, "summaryStats");

  const query = useReviewProjectImpactComputeCostQuery({ variables: { readyPlanId, readyPlanOptionIds } });

  return queryResult(query, ({ computeReadyPlanCost }) => {
    const { protoProjectItems } = computeReadyPlanCost;
    // The initial workflow replaces the whole configuration, so the computed items *are* the new scope
    const rows = createProjectBudgetRows(
      projectCostCodes,
      protoProjectItems.map((ppi) => ({
        costCode: ppi.item.costCode,
        totalCostInCents: ppi.totalCostInCents,
        // `computeReadyPlanCost` only prices the trade cost, so there is no price to roll up
        totalPriceInCents: undefined,
      })),
      true,
    );
    const { currentBudgetInCents, newBudgetInCents } = reviewImpactBudgetTotals(projectCostCodes, rows);
    // Mocked alongside the Review Impact step's schedule table — see the TODO in `ScheduleImpactTable`.
    const changeInDays = mockScheduleImpactMilestones().last!.changeInDays;

    return (
      <FormLines labelStyle="left" width="full" compact>
        <FieldGroup>
          <StaticField label={`Chargeable to ${clientNoun}`} value={isChargeable ? "Yes" : "No"} {...tid.chargeable} />
          <StaticField label="Scope Impact" value={count(protoProjectItems.length, "item")} {...tid.scopeImpact} />
        </FieldGroup>
        <FieldGroup>
          <StaticField label="Current Budget" {...tid.currentBudget}>
            <Price valueInCents={currentBudgetInCents} />
          </StaticField>
          <StaticField
            label="Schedule Impact"
            value={`+${changeInDays} ${pluralize(changeInDays, "day")}`}
            {...tid.scheduleImpact}
          />
        </FieldGroup>
        <FieldGroup>
          <StaticField label="New Budget" {...tid.newBudget}>
            <Price valueInCents={newBudgetInCents} />
          </StaticField>
          <StaticField label="Created by" value={currentUser.name} {...tid.createdBy} />
        </FieldGroup>
      </FormLines>
    );
  });
}
